import api from './api';
import { reportUploadProgress } from '../utils/uploadProgress';
import { Category, Content, MonthlyTheme } from '../types';

/** GET /admin/categories/:id/content-order */
export type CategoryDisplayOrderGetResponse =
  | {
      mode: 'subcategories';
      subcategoryIds: string[];
      items: { _id: string; name: string }[];
    }
  | {
      mode: 'content';
      contentIds: string[];
      items: { _id: string; title: string }[];
    };

/** PATCH /admin/categories/:id/content-order */
export type CategoryDisplayOrderPatchResponse =
  | { message: string; mode: 'subcategories'; subcategoryIds: string[] }
  | { message: string; mode: 'content'; contentIds: string[] };

// Auth Services
export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (formData: FormData) => {
    const response = await api.patch('/auth/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Category Services
export const categoryService = {
  getAll: async (page?: number, limit?: number): Promise<any> => {
    const params: any = {};
    if (page) params.page = page;
    if (limit) params.limit = limit;

    const response = await api.get('/admin/categories', { params });
    return response.data;
  },

  /** Get all parent categories only (no pagination). Use for sidebar. */
  getParents: async (includeInactive = false): Promise<Category[]> => {
    const response = await api.get('/admin/categories/parents', {
      params: includeInactive ? { includeInactive: true } : {},
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  /** Get all categories as parent + nested subcategories (no pagination). Use for sidebar with subcategories. */
  getAllHierarchical: async (includeInactive = false): Promise<any[]> => {
    const response = await api.get('/admin/categories', {
      params: { hierarchical: true, ...(includeInactive ? { includeInactive: true } : {}) },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  getOne: async (id: string): Promise<Category> => {
    const response = await api.get(`/admin/categories/${id}`);
    return response.data;
  },

  getChildren: async (id: string, page?: number, limit?: number): Promise<any> => {
    const params: any = {};
    if (page) params.page = page;
    if (limit) params.limit = limit;
    
    const response = await api.get(`/admin/categories/${id}/children`, { params });
    return response.data;
  },

  create: async (formData: FormData): Promise<Category> => {
    const response = await api.post('/admin/categories', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, formData: FormData): Promise<Category> => {
    const response = await api.patch(`/admin/categories/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/categories/${id}`);
  },

  getCategoryContentOrder: async (
    categoryId: string,
  ): Promise<CategoryDisplayOrderGetResponse> => {
    const response = await api.get(`/admin/categories/${categoryId}/content-order`);
    return response.data;
  },

  /** Send exactly one of contentIds or subcategoryIds (matches GET response `mode`). */
  updateCategoryDisplayOrder: async (
    categoryId: string,
    body: { contentIds: string[] } | { subcategoryIds: string[] },
  ): Promise<CategoryDisplayOrderPatchResponse> => {
    const response = await api.patch(`/admin/categories/${categoryId}/content-order`, body);
    return response.data;
  },
};

// Content Services
export const contentService = {
  getAll: async (params?: { search?: string; page?: number; limit?: number; categoryId?: string; contentType?: 'single' | 'module' | 'all'; status?: string }): Promise<any> => {
    const response = await api.get('/admin/content', { params });
    return response.data;
  },

  getOne: async (id: string): Promise<Content> => {
    const response = await api.get(`/admin/content/${id}`);
    return response.data;
  },

  create: async (formData: FormData): Promise<Content> => {
    const response = await api.post('/admin/content', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, formData: FormData): Promise<Content> => {
    const response = await api.patch(`/admin/content/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadModuleMedia: async (
    id: string,
    moduleIndex: number,
    formData: FormData,
    onUploadProgress?: (percent: number) => void,
  ): Promise<{ moduleIndex: number; module: Record<string, unknown> }> => {
    const response = await api.post(
      `/admin/content/${id}/modules/${moduleIndex}/media`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (event) => reportUploadProgress(event, onUploadProgress),
      },
    );
    return response.data;
  },

  removeModuleMedia: async (
    id: string,
    moduleIndex: number,
    target: 'file' | 'thumbnail',
  ): Promise<{ moduleIndex: number; module: Record<string, unknown> }> => {
    const response = await api.delete(`/admin/content/${id}/modules/${moduleIndex}/media`, {
      params: { target },
    });
    return response.data;
  },

  uploadIntroductionMedia: async (
    id: string,
    introIndex: number,
    formData: FormData,
    onUploadProgress?: (percent: number) => void,
  ): Promise<{ introIndex: number; introduction: Record<string, unknown> }> => {
    const response = await api.post(
      `/admin/content/${id}/introductions/${introIndex}/media`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (event) => reportUploadProgress(event, onUploadProgress),
      },
    );
    return response.data;
  },

  removeIntroductionMedia: async (
    id: string,
    introIndex: number,
    target: 'file' | 'thumbnail',
  ): Promise<{ introIndex: number; introduction: Record<string, unknown> }> => {
    const response = await api.delete(`/admin/content/${id}/introductions/${introIndex}/media`, {
      params: { target },
    });
    return response.data;
  },

  uploadContentMedia: async (
    id: string,
    formData: FormData,
    onUploadProgress?: (percent: number) => void,
  ): Promise<{ content: Record<string, unknown> }> => {
    const response = await api.post(`/admin/content/${id}/media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (event) => reportUploadProgress(event, onUploadProgress),
    });
    return response.data;
  },

  removeContentMedia: async (
    id: string,
    target: 'file' | 'thumbnail',
  ): Promise<{ content: Record<string, unknown> }> => {
    const response = await api.delete(`/admin/content/${id}/media`, {
      params: { target },
    });
    return response.data;
  },

  uploadThumbnail: async (id: string, formData: FormData): Promise<Content> => {
    const response = await api.post(`/admin/content/${id}/thumbnail`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/content/${id}`);
  },

  cleanupUploads: async (): Promise<{ deleted: number; freedBytes: number }> => {
    const response = await api.post('/admin/content/cleanup-uploads');
    return response.data;
  },
};

// Oracle Cards - Admin
export const oracleCardsService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<{ items: any[]; total: number; page: number; limit: number; totalPages: number }> => {
    const response = await api.get('/admin/oracle-cards', { params });
    return response.data;
  },

  getOne: async (id: string): Promise<any> => {
    const response = await api.get(`/admin/oracle-cards/${id}`);
    return response.data;
  },

  create: async (formData: FormData): Promise<any> => {
    const response = await api.post('/admin/oracle-cards', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, formData: FormData): Promise<any> => {
    const response = await api.patch(`/admin/oracle-cards/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/oracle-cards/${id}`);
  },

  setIsActive: async (id: string, isActive: boolean): Promise<any> => {
    const fd = new FormData();
    fd.append('isActive', isActive ? 'true' : 'false');
    const response = await api.patch(`/admin/oracle-cards/${id}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getSchedule: async (date: string): Promise<{ date?: string; cardIds: any[] }> => {
    const response = await api.get('/admin/oracle-cards/schedule', { params: { date } });
    return response.data;
  },

  setSchedule: async (date: string, cardIds: string[]): Promise<any> => {
    const response = await api.post('/admin/oracle-cards/schedule', { date, cardIds });
    return response.data;
  },

  getSettings: async (): Promise<{ mainImage: string | null }> => {
    const response = await api.get('/admin/oracle-cards/settings');
    return response.data;
  },

  updateMainImage: async (file: File): Promise<{ mainImage: string }> => {
    const fd = new FormData();
    fd.append('mainImage', file);
    const response = await api.patch('/admin/oracle-cards/settings/main-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Diary (Tags & Calendar Events) - Admin
export const diaryService = {
  getTags: async (activeOnly = true): Promise<any[]> => {
    const response = await api.get('/admin/diary/tags', {
      params: { activeOnly: activeOnly !== false },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  createTag: async (data: { name: string; color?: string }) => {
    const response = await api.post('/admin/diary/tags', data);
    return response.data;
  },

  updateTag: async (id: string, data: { name?: string; color?: string }) => {
    const response = await api.patch(`/admin/diary/tags/${id}`, data);
    return response.data;
  },

  deleteTag: async (id: string) => {
    await api.delete(`/admin/diary/tags/${id}`);
  },

  getEventTypes: async (): Promise<import('../types').EventType[]> => {
    const response = await api.get('/admin/diary/event-types');
    return Array.isArray(response.data) ? response.data : [];
  },

  createEventType: async (data: { name: string; color?: string }) => {
    const response = await api.post('/admin/diary/event-types', data);
    return response.data;
  },

  updateEventType: async (id: string, data: { name?: string; color?: string }) => {
    const response = await api.patch(`/admin/diary/event-types/${id}`, data);
    return response.data;
  },

  deleteEventType: async (id: string) => {
    await api.delete(`/admin/diary/event-types/${id}`);
  },

  getCalendarEvents: async (): Promise<any[]> => {
    const response = await api.get('/admin/diary/calendar-events');
    return Array.isArray(response.data) ? response.data : [];
  },

  createCalendarEvent: async (data: {
    eventType: string;
    date: string;
    timezone?: string;
    startTime?: string;
    endTime?: string;
    title?: string;
    shortDesc?: string;
    description?: string;
    webLink?: string;
    color?: string;
  }) => {
    const response = await api.post('/admin/diary/calendar-events', data);
    return response.data;
  },

  getCalendarEvent: async (eventId: string): Promise<any> => {
    const response = await api.get(`/admin/diary/calendar-events/${eventId}`);
    return response.data;
  },

  updateCalendarEvent: async (eventId: string, data: {
    eventType?: string;
    date?: string;
    timezone?: string;
    startTime?: string;
    endTime?: string;
    title?: string;
    shortDesc?: string;
    description?: string;
    webLink?: string;
    color?: string;
  }) => {
    const response = await api.patch(`/admin/diary/calendar-events/${eventId}`, data);
    return response.data;
  },

  getEventJoinedUsers: async (eventId: string): Promise<{ event: any; users: any[] }> => {
    const response = await api.get(`/admin/diary/calendar-events/${eventId}/joined-users`);
    return response.data;
  },

  deleteCalendarEvent: async (id: string) => {
    await api.delete(`/admin/diary/calendar-events/${id}`);
  },
};

// Monthly Theme - Admin
export const monthlyThemeService = {
  getAll: async (activeOnly?: boolean): Promise<MonthlyTheme[]> => {
    const response = await api.get('/admin/monthly-theme', {
      params: activeOnly === undefined ? {} : { activeOnly },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  create: async (formData: FormData): Promise<MonthlyTheme> => {
    const response = await api.post('/admin/monthly-theme', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, formData: FormData): Promise<MonthlyTheme> => {
    const response = await api.patch(`/admin/monthly-theme/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/monthly-theme/${id}`);
  },
};

// User Services
export type AdminCreateUserPayload = {
  name: string;
  email: string;
  password: string;
  role?: 'user' | 'admin';
  emailVerified?: boolean;
  isActive?: boolean;
  subscriptionPlan?: 'none' | 'monthly' | 'yearly';
};

export const userService = {
  getAll: async (page = 1, limit = 20): Promise<{ items: any[]; total: number; page: number; limit: number; totalPages: number }> => {
    try {
      const response = await api.get('/admin/users', { params: { page, limit } });
      return response.data;
    } catch (err) {
      console.warn('User endpoints error', err);
      return { items: [], total: 0, page: 1, limit: 20, totalPages: 0 };
    }
  },

  create: async (data: AdminCreateUserPayload) => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  update: async (id: string, data: any) => {
    const response = await api.patch(`/admin/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    await api.delete(`/admin/users/${id}`);
  },

  reactivate: async (id: string, reason?: string) => {
    const payload = reason?.trim() ? { reason: reason.trim() } : {};
    const response = await api.patch(`/admin/users/${id}/reactivate`, payload);
    return response.data;
  },

  updateStatus: async (id: string, isActive: boolean) => {
    const response = await api.patch(`/admin/users/${id}/status`, { isActive });
    return response.data;
  },
};

// Dashboard stats (aggregates from existing admin APIs)
export const dashboardService = {
  getStats: async (): Promise<{
    totalContent: number;
    totalUsers: number;
    totalCategories: number;
    parentCategoriesCount: number;
    contentByStatus: { published: number; draft: number; archived: number };
  }> => {
    const [contentRes, contentPublished, contentDraft, contentArchived, usersRes, categoriesRes, parents] = await Promise.all([
      contentService.getAll({ page: 1, limit: 1 }),
      contentService.getAll({ page: 1, limit: 1, status: 'published' }),
      contentService.getAll({ page: 1, limit: 1, status: 'draft' }),
      contentService.getAll({ page: 1, limit: 1, status: 'archived' }),
      userService.getAll(1, 1),
      categoryService.getAll(1, 1),
      categoryService.getParents(),
    ]);

    const totalContent = contentRes?.pagination?.total ?? contentRes?.data?.length ?? 0;
    const totalUsers = usersRes?.total ?? 0;
    const totalCategories = categoriesRes?.pagination?.total ?? categoriesRes?.data?.length ?? 0;
    const parentCategoriesCount = Array.isArray(parents) ? parents.length : 0;

    const contentByStatus = {
      published: contentPublished?.pagination?.total ?? contentPublished?.data?.length ?? 0,
      draft: contentDraft?.pagination?.total ?? contentDraft?.data?.length ?? 0,
      archived: contentArchived?.pagination?.total ?? contentArchived?.data?.length ?? 0,
    };

    return {
      totalContent,
      totalUsers,
      totalCategories,
      parentCategoriesCount,
      contentByStatus,
    };
  },
};

// Notifications (user-scoped: admin receives e.g. NEW_USER_REGISTERED)
export const notificationService = {
  getList: async (params?: {
    page?: number;
    limit?: number;
    read?: boolean;
    type?: string;
  }): Promise<{ items: any[]; total: number; page: number; limit: number; totalPages: number }> => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread-count');
    return response.data?.count ?? response.data ?? 0;
  },

  markAsRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },

  markAllAsRead: async (): Promise<void> => {
    await api.post('/notifications/mark-all-read');
  },
};

// Subscriptions / Invoices - Admin
export const subscriptionService = {
  listInvoices: async (params?: {
    status?: string;
    userId?: string;
    page?: number;
    limit?: number;
  }): Promise<import('../types').InvoicesListResponse> => {
    const response = await api.get('/admin/subscriptions/invoices', { params });
    return response.data;
  },

  getNotSubscribedUsers: async (limit?: number): Promise<import('../types').NotSubscribedUser[]> => {
    const response = await api.get('/admin/subscriptions/not-subscribed-users', {
      params: limit ? { limit } : undefined,
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  updateSubscriptionStatus: async (subscriptionId: string, status: string) => {
    const response = await api.patch(`/admin/subscriptions/${subscriptionId}/status`, { status });
    return response.data;
  },

  verifyLiveSubscription: async (subscriptionId: string) => {
    const response = await api.get(`/subscriptions/${subscriptionId}/verify-live`);
    return response.data;
  },

  sendNotSubscribedEmail: async (data: {
    subject: string;
    message: string;
    userId?: string;
    recipientIds?: string[];
  }) => {
    const response = await api.post('/admin/subscriptions/send-not-subscribed-email', data);
    return response.data;
  },

  stripeLookup: async (id: string) => {
    throw new Error('Stripe has been removed. This endpoint is no longer available.');
  },
};

// Deeper Oracle Reading - Admin (accept/reject requests, upload video)
export const oracleReadingService = {
  getOrders: async (status?: string): Promise<import('../types').OracleReadingOrder[]> => {
    const response = await api.get('/admin/oracle-reading/orders', {
      params: status ? { status } : {},
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  acceptOrder: async (orderId: string): Promise<{ message: string }> => {
    const response = await api.post(`/admin/oracle-reading/orders/${orderId}/accept`);
    return response.data;
  },

  rejectOrder: async (orderId: string): Promise<{ message: string }> => {
    const response = await api.post(`/admin/oracle-reading/orders/${orderId}/reject`);
    return response.data;
  },

  /**
   * Complete order: video (required), title (required), optional thumbnail, shortDesc, longDesc.
   */
  completeOrder: async (
    orderId: string,
    options: {
      videoFile: File;
      title: string;
      shortDesc?: string;
      longDesc?: string;
      thumbnailFile?: File;
    }
  ): Promise<{ message: string }> => {
    const formData = new FormData();
    formData.append('video', options.videoFile);
    formData.append('title', options.title.trim());
    if (options.shortDesc != null && options.shortDesc.trim() !== '') {
      formData.append('shortDesc', options.shortDesc.trim());
    }
    if (options.longDesc != null && options.longDesc.trim() !== '') {
      formData.append('longDesc', options.longDesc.trim());
    }
    if (options.thumbnailFile) {
      formData.append('thumbnail', options.thumbnailFile);
    }
    const response = await api.post(
      `/admin/oracle-reading/orders/${orderId}/complete`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Patch order details (delivered orders only). At least one of title, shortDesc, longDesc, videoFile, or thumbnailFile.
   */
  patchOrder: async (
    orderId: string,
    options: {
      title?: string;
      shortDesc?: string;
      longDesc?: string;
      videoFile?: File;
      thumbnailFile?: File;
    }
  ): Promise<{ message: string }> => {
    const formData = new FormData();
    if (options.title !== undefined) {
      formData.append('title', options.title.trim());
    }
    if (options.shortDesc !== undefined) {
      formData.append('shortDesc', (options.shortDesc ?? '').trim());
    }
    if (options.longDesc !== undefined) {
      formData.append('longDesc', (options.longDesc ?? '').trim());
    }
    if (options.videoFile) {
      formData.append('video', options.videoFile);
    }
    if (options.thumbnailFile) {
      formData.append('thumbnail', options.thumbnailFile);
    }
    const response = await api.patch(
      `/admin/oracle-reading/orders/${orderId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};

// Home screen section order (matches top-level key order of GET /user/home)
export const homeLayoutService = {
  getSectionOrder: async (): Promise<{ sectionOrder: string[]; sectionLabels: Record<string, string> }> => {
    const response = await api.get('/admin/home/section-order');
    return response.data;
  },

  updateSectionOrder: async (
    sectionOrder: string[]
  ): Promise<{ message: string; sectionOrder: string[] }> => {
    const response = await api.patch('/admin/home/section-order', { sectionOrder });
    return response.data;
  },
};

// Home section images (Oracle Card & Deeper Oracle Reading – shown in app Home API)
export const homeImagesService = {
  uploadOracleCardImage: async (file: File): Promise<{ message: string; path: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/admin/home-images/oracle-card', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadDeeperOracleReadingImage: async (file: File): Promise<{ message: string; path: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/admin/home-images/deeper-oracle-reading', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
